// BookingTable.jsx
import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import DataTable from "../DataTable";
import BookingFilterBar from "./booking_filter_bar";
import moment from "moment";
import { useAuthz } from "../../auth/useAuthz";
import {
  DELETE_BOOKING,
  UPDATE_SERVICE_STATUS,
  UPDATE_ORDER_CALLING_STATUS,
  ACKNOWLEDGE_BOOKING,
  RECORD_MANUAL_ONLINE_PAYMENT,
} from "./graphql";
import { useMutation } from "@apollo/client";
import { is } from "date-fns/locale";
import OrderDetailsModal from "./OrderDetailsModal";

const IssueTypeLabel = {
  customerNotAvailable: "Customer Not Available",
  notPickingUp: "Not Picking Up Calls",
  wrongAddress: "Wrong Address",
  paymentDispute: "Payment Dispute",
  safetyConcern: "Safety Concern",
  technicalProblem: "Technical Problem",
  serviceInterruption: "Service Interruption",
  partialCompletion: "Partial Completion",
  other: "Other",
};

function formatIssueType(type) {
  return IssueTypeLabel[type] || "Unknown";
}

// Statuses an unresolved issue can be flagged on. Includes "Started"/"Scanned"
// since a "can't continue" (service interruption) report happens mid-service,
// not before it - excluding those statuses hid the flag for exactly the
// bookings this feature exists for.
const ISSUE_FLAGGABLE_STATUSES = [
  "Pending",
  "Confirmed",
  "Headed",
  "Reached",
  "Started",
  "Scanned",
];

// A partial completion ("Can't continue?" -> Complete service now with some
// services not done) is only ever raised as the booking completes, so it
// has to stay visible on Completed bookings too.
const isIssueFlaggable = (booking) =>
  ISSUE_FLAGGABLE_STATUSES.includes(booking.status) ||
  (booking.status === "Completed" && booking.issueType === "partialCompletion");
/**
 * Props:
 *  - data
 *  - isUpcomingBooking
 *  - setIsUpcomingBooking (applyUpcomingToggle from parent)
 *  - openEditForm (function booking => void)
 *  - setEditingData (optional)
 *  - setCreateBookingForm
 *  - bookingFilter
 *  - setBookingFilter
 *  - loading
 *
 * Optional environment:
 *  - REACT_APP_GRAPHQL_ENDPOINT (fallback: "/graphql")
 *  - auth token from localStorage key "token" or "authToken" (change as needed)
 */
const BookingTable = ({
  data,
  isUpcomingBooking,
  setIsUpcomingBooking,
  openEditForm,
  setEditingData,
  setCreateBookingForm,
  bookingFilter,
  setBookingFilter,
  loading,
  isFromAdminDashboard = false, // if true, shows less columns and no actions
  onRefresh,
  unacknowledgedCount = 0,
}) => {
  const { can } = useAuthz();
  const [sanitizedData, setSanitizedData] = useState([]);
  // track ids currently being deleted (so we can disable the delete button)
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [deleteBooking, { deleteLoading, deleteError }] =
    useMutation(DELETE_BOOKING);
  const [resolveIssue, { loading: resolvingLoading }] = useMutation(
    UPDATE_SERVICE_STATUS,
  );
  const [acknowledgeBookingMutation] = useMutation(ACKNOWLEDGE_BOOKING);
  const [acknowledgingIds, setAcknowledgingIds] = useState(new Set());
  const [expandedRow, setExpandedRow] = useState(null);
  // Anchor position (viewport coords) for the issue-details popup, captured
  // from the triggering badge on click. Rendered via a portal to
  // document.body since it lives inside a sticky table column - nesting it
  // there traps it in that column's stacking context, so a later row (a
  // sibling stacking context) always renders on top of it regardless of
  // z-index, no matter how high.
  const [issuePopupAnchor, setIssuePopupAnchor] = useState(null);
  // Same portal treatment for the desktop three-dot menu + the Remarks/
  // Payment popups it opens - all three are only ever triggered from that
  // one button, so one shared anchor is enough.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  // Booking currently shown in the read-only "View Details" tabbed modal
  const [viewingBooking, setViewingBooking] = useState(null);
  // Mobile card view: which card has its extra details expanded
  const [expandedCardId, setExpandedCardId] = useState(null);
  const toggleCardDetails = (id) => {
    setExpandedCardId((prev) => (prev === id ? null : id));
  };

  const [searchType, setSearchType] = useState(bookingFilter?.contactNumber ? "contactNumber" : "bookingId");
  const [searchValue, setSearchValue] = useState(bookingFilter?.contactNumber || bookingFilter?.bookingId || "");

  const handleSearchChange = (type, value) => {
    setSearchType(type);
    setSearchValue(value);
  };

  const handleApplySearch = () => {
    let validBookingId = null;
    let validContact = null;

    if (searchType === "bookingId" && searchValue.trim()) {
      validBookingId = searchValue.trim();
    } else if (searchType === "contactNumber" && searchValue.trim()) {
      validContact = searchValue.trim();
    }

    setBookingFilter((prev) => ({
      ...prev,
      bookingId: validBookingId,
      contactNumber: validContact,
    }));
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setBookingFilter((prev) => ({
      ...prev,
      bookingId: null,
      contactNumber: null,
    }));
  };

  const toggleRow = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const toggleIssuePopup = (id, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setIssuePopupAnchor({ top: rect.bottom + 6, left: rect.left });
    toggleRow(id);
  };

  // Remarks: a general admin note log on the booking, independent of the
  // issue-tracking popup above and of the calling-status fields edited
  // elsewhere — added directly from the table so admins don't need to open
  // a separate form just to leave a note.
  const [expandedRemarkRow, setExpandedRemarkRow] = useState(null);
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [savingRemarkIds, setSavingRemarkIds] = useState(new Set());
  const [addRemark] = useMutation(UPDATE_ORDER_CALLING_STATUS);

  const toggleRemarkRow = (id) => {
    setExpandedRemarkRow((prev) => (prev === id ? null : id));
  };

  // Record Payment: lets an admin attribute an online payment the customer
  // made via the admin's own/static QR (not tied to any booking
  // automatically) directly from the list, without opening the full
  // verify/edit form.
  const [expandedPaymentRow, setExpandedPaymentRow] = useState(null);
  const [paymentDrafts, setPaymentDrafts] = useState({});
  const [savingPaymentIds, setSavingPaymentIds] = useState(new Set());
  const [recordManualOnlinePayment] = useMutation(
    RECORD_MANUAL_ONLINE_PAYMENT,
  );

  const togglePaymentRow = (id) => {
    setExpandedPaymentRow((prev) => (prev === id ? null : id));
  };

  const getPaymentDraft = (idKey) =>
    paymentDrafts[idKey] || { amount: "", referenceId: "", note: "" };

  const updatePaymentDraft = (idKey, field, value) => {
    setPaymentDrafts((prev) => ({
      ...prev,
      [idKey]: { ...getPaymentDraft(idKey), [field]: value },
    }));
  };

  const handleRecordPayment = useCallback(
    async (booking) => {
      const idKey = booking.id || booking.bookingId;
      const draft = paymentDrafts[idKey] || {};
      const amount = Number(draft.amount);

      if (!draft.amount || Number.isNaN(amount) || amount <= 0) {
        window.alert("Enter a valid amount greater than 0");
        return;
      }

      setSavingPaymentIds((prev) => new Set(prev).add(idKey));
      try {
        const res = await recordManualOnlinePayment({
          variables: {
            data: {
              orderId: booking.id,
              amount,
              referenceId: draft.referenceId || undefined,
              note: draft.note || undefined,
            },
          },
        });

        const out = res?.data?.recordManualOnlinePayment;
        if (out?.status === "SUCCESS") {
          setSanitizedData((prev) =>
            prev.map((b) =>
              (b.id || b.bookingId) === idKey
                ? {
                    ...b,
                    amountPaid: out.amountPaid,
                    adminRecordedAmount: out.adminRecordedAmount,
                  }
                : b,
            ),
          );
          setPaymentDrafts((prev) => ({
            ...prev,
            [idKey]: { amount: "", referenceId: "", note: "" },
          }));
          setExpandedPaymentRow(null);
          window.alert(
            `Payment recorded. ₹${out.amountDue} still pending for this booking.`,
          );
        } else {
          window.alert(out?.message || "Failed to record payment");
        }
      } catch (err) {
        console.error("Failed to record payment:", err);
        window.alert("Failed to record payment. Please try again.");
      } finally {
        setSavingPaymentIds((prev) => {
          const next = new Set(prev);
          next.delete(idKey);
          return next;
        });
      }
    },
    [paymentDrafts, recordManualOnlinePayment],
  );

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
      setExpandedRemarkRow(null);
      setExpandedPaymentRow(null);
      setExpandedRow(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleAddRemark = useCallback(
    async (booking) => {
      const idKey = booking.id || booking.bookingId;
      const text = (remarkDrafts[idKey] || "").trim();
      if (!text) return;

      setSavingRemarkIds((prev) => new Set(prev).add(idKey));
      try {
        const res = await addRemark({
          variables: { data: { orderId: booking.id, adminRemark: text } },
        });

        if (res.data.updateOrderCallingStatus.status === "SUCCESS") {
          const updatedRemark =
            res.data.updateOrderCallingStatus.order?.adminRemark || "";
          setSanitizedData((prev) =>
            prev.map((b) =>
              (b.id || b.bookingId) === idKey
                ? { ...b, adminRemark: updatedRemark }
                : b,
            ),
          );
          setRemarkDrafts((prev) => ({ ...prev, [idKey]: "" }));
        } else {
          window.alert(
            res.data.updateOrderCallingStatus.message ||
              "Failed to add remark",
          );
        }
      } catch (err) {
        console.error("Failed to add remark:", err);
        window.alert("Failed to add remark. Please try again.");
      } finally {
        setSavingRemarkIds((prev) => {
          const next = new Set(prev);
          next.delete(idKey);
          return next;
        });
      }
    },
    [remarkDrafts, addRemark],
  );

  useEffect(() => {
    const arr = Array.isArray(data) ? data : [];
    const clean = arr.map((b, idx) => {
      if (!b || typeof b !== "object") {
        console.warn("Booking item is not an object at index", idx, b);
        return {
          id: `missing-${idx}-${String(Math.random()).slice(2)}`,
          bookingId: "N/A",
          orderDetail: [],
        };
      }
      return {
        ...b,
        orderDetail: Array.isArray(b.orderDetail) ? b.orderDetail : [],
      };
    });

    // For upcoming bookings, sort ascending by date then by time slot so
    // today's earliest appointment (e.g. 8 AM) always appears at the top.
    if (isUpcomingBooking) {
      clean.sort((a, b) => {
        // Parse "DD/MM/YYYY" or ISO date strings into comparable values
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          // Handle "DD/MM/YYYY" format
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
            const [dd, mm, yyyy] = dateStr.split("/");
            return new Date(`${yyyy}-${mm}-${dd}`).getTime();
          }
          return new Date(dateStr).getTime();
        };

        // Parse "8:00 AM", "9:30 PM", etc. into minutes since midnight
        const parseTime = (timeStr) => {
          if (!timeStr) return 0;
          const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
          if (!match) return 0;
          let hours = parseInt(match[1], 10);
          const minutes = parseInt(match[2], 10);
          const period = match[3] ? match[3].toUpperCase() : null;
          if (period === "PM" && hours !== 12) hours += 12;
          if (period === "AM" && hours === 12) hours = 0;
          return hours * 60 + minutes;
        };

        const dateA = parseDate(a.bookingDate);
        const dateB = parseDate(b.bookingDate);
        if (dateA !== dateB) return dateA - dateB;
        return parseTime(a.timeSlot) - parseTime(b.timeSlot);
      });
    }

    setSanitizedData(clean);
  }, [data, isUpcomingBooking]);

  // handle edit: call parent's openEditForm(book)
  const handleEditClick = useCallback(
    (booking) => {
      if (typeof openEditForm === "function") {
        openEditForm(booking);
      } else {
        if (typeof setEditingData === "function") setEditingData(booking);
      }
    },
    [openEditForm, setEditingData],
  );

  const handleUpcomingBookingClick = useCallback(() => {
    setIsUpcomingBooking(true);
  }, [setIsUpcomingBooking]);

  const handlePreviousBookingClick = useCallback(() => {
    setIsUpcomingBooking(false);
  }, [setIsUpcomingBooking]);

  const handleCreateBookingClick = useCallback(() => {
    setCreateBookingForm(true);
  }, [setCreateBookingForm]);

  /**
   * Delete flow:
   *  - confirm
   *  - optimistic remove from UI
   *  - call GraphQL mutation
   *  - if success: keep removed; optionally show toast (not implemented here)
   *  - if fail: restore and show console.error (or call parent's error handler)
   *
   * Note: by default this uses:
   *   endpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT || "/graphql"
   *   token from localStorage.getItem("token") || localStorage.getItem("authToken")
   *
   * Adjust those to fit your app (or replace fetch with your GraphQL client).
   */
  const handleDeleteClick = useCallback(
    async (booking) => {
      // derive id to send to API (user used `deleteBookings(data: ID!)`)
      const idToDelete = booking.id || booking.bookingId;
      if (!idToDelete) {
        console.error("No id found on booking to delete", booking);
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to delete booking ${
          booking.bookingId || idToDelete
        }? This action cannot be undone.`,
      );
      if (!confirmed) return;

      // Optimistic UI update: remove locally, but keep a copy to rollback if needed
      const prev = sanitizedData;
      const newData = prev.filter(
        (b) => (b.id || b.bookingId) !== (booking.id || booking.bookingId),
      );

      // mark as deleting

      deleteBooking({
        variables: { data: idToDelete },
      })
        .then((res) => {
          if (res.data.deleteBookings.status === "SUCCESS") {
            setSanitizedData(newData);
          } else {
            console.warn(
              "deleteBookings returned non-success status:",
              res.data.deleteBookings.message,
            );
            throw new Error(res.data.deleteBookings.message || "Delete failed");
          }
        })
        .catch((err) => {
          console.error("Failed to delete booking:", err);
          setSanitizedData(prev);
        });
    },
    [sanitizedData],
  );

  const handleResolveClick = useCallback(
    async (booking) => {
      const idToResolve = booking.id || booking.bookingId;
      if (!idToResolve) return;

      const resolutionText = window.prompt(
        `Enter resolution details for booking ${booking.bookingId || idToResolve}:`,
      );
      if (resolutionText === null) return; // User cancelled

      try {
        const res = await resolveIssue({
          variables: {
            data: {
              id: idToResolve,
              state: "Issue_Resolved",
              issueDescription: resolutionText,
            },
          },
        });
        if (res.data.updateServiceStatus.status === "SUCCESS") {
          // Optimistically update UI
          setSanitizedData((prev) =>
            prev.map((b) => {
              if ((b.id || b.bookingId) === idToResolve) {
                return {
                  ...b,
                  issueResolvedAt: new Date().toISOString(),
                  issueResolutionDetails: resolutionText || "Issue resolved",
                };
              }
              return b;
            }),
          );
        } else {
          window.alert(
            res.data.updateServiceStatus.message || "Failed to resolve issue",
          );
        }
      } catch (err) {
        console.error("Failed to resolve issue:", err);
        window.alert("Failed to resolve issue. Please try again.");
      }
    },
    [resolveIssue],
  );

  return (
    <div className="flex flex-col space-y-6">
      {!isFromAdminDashboard && (
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 px-2 md:px-0">
          <div className="flex items-center font-jost text-xl font-bold space-x-3 pt-4 md:pt-8 md:pl-5 text-[#FBB033]">
            <p
              className={`cursor-pointer text-base px-2 py-1 font-semibold ${
                isUpcomingBooking ? "bg-white text-[#00395E]" : "text-[#FBB034]"
              }`}
              onClick={handleUpcomingBookingClick}
            >
              Upcoming Bookings
            </p>
            <p
              className={`cursor-pointer text-base px-2 py-1 font-semibold ${
                !isUpcomingBooking
                  ? "bg-white text-[#00395E]"
                  : "text-[#FBB034]"
              }`}
              onClick={handlePreviousBookingClick}
            >
              Previous Bookings
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            {/* Global Search next to Refresh button */}
            <div className="flex bg-[#0c0b0b] border border-[#4B5563] rounded-md overflow-hidden w-full sm:min-w-[250px] sm:w-auto">
              <select
                className="bg-[#1a1a1a] text-white px-2 py-1.5 outline-none border-r border-[#4B5563] text-sm"
                value={searchType}
                onChange={(e) => handleSearchChange(e.target.value, searchValue)}
              >
                <option value="bookingId">Booking ID</option>
                <option value="contactNumber">Phone Number</option>
              </select>
              <input
                type="text"
                placeholder={`Search by ${searchType === "bookingId" ? "Booking ID" : "Phone"}`}
                className="bg-transparent text-white px-2 py-1.5 outline-none w-full text-sm min-w-0"
                value={searchValue}
                onChange={(e) => handleSearchChange(searchType, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleApplySearch();
                }}
              />
              {searchValue && (
                <button
                  className="text-gray-400 hover:text-white px-2"
                  onClick={handleClearSearch}
                >
                  ✕
                </button>
              )}
              <button
                className="bg-[#FBB033] text-black px-3 py-1.5 text-sm font-semibold hover:bg-[#e5a02f]"
                onClick={handleApplySearch}
              >
                Search
              </button>
            </div>

            <div className="flex gap-2">
              {onRefresh && (
                <button
                  className="flex-1 sm:flex-initial font-be-jost-pro font-medium text-sm border border-[#FBB033] text-[#FBB033] rounded-md px-5 py-2 hover:bg-[#FBB033] hover:text-black transition-colors"
                  onClick={onRefresh}
                  disabled={loading}
                >
                  Refresh
                </button>
              )}
              {can("bookings.create") && (
                <button
                  className="flex-1 sm:flex-initial font-be-jost-pro font-medium text-sm border border-[#FBB033] text-[#FBB033] rounded-md px-5 py-2 hover:bg-[#FBB033] hover:text-black transition-colors"
                  onClick={handleCreateBookingClick}
                >
                  Create Booking
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!isFromAdminDashboard && (
        <BookingFilterBar
          isPreviousBooking={!isUpcomingBooking}
          onFilterChange={setBookingFilter}
          currentFilter={bookingFilter}
          issuedReportedBookings={
            sanitizedData.filter(
              (b) =>
                b.issueType &&
                !b.issueResolvedAt &&
                isIssueFlaggable(b),
            ).length
          }
          bookingsWithRemark={
            sanitizedData.filter((b) => b.adminRemark).length
          }
          unacknowledgedCount={
            sanitizedData.filter((b) => !b.isAdminAcknowledged).length
          }
          dummyEmployeeCount={
            sanitizedData.filter((b) => b.employee?.isDummy).length
          }
          blockedUserCount={
            sanitizedData.filter((b) => b.user?.isBlocked).length
          }
          noShowAwaitingCount={
            sanitizedData.filter(
              (b) => b.noShowHoldStatus === "AWAITING_RESPONSE",
            ).length
          }
        />
      )}

      {loading && (
        <div className="flex justify-center items-center py-3 text-sm text-[#FBB033]">
          <span className="mr-2 animate-spin rounded-full h-4 w-4 border-t border-b border-[#FBB033]" />
          <span>Updating bookings…</span>
        </div>
      )}

      {/* Desktop / tablet: table view */}
      {/* overflow-y-visible is needed alongside overflow-x-auto: per the CSS
          spec, setting only overflow-x to a non-visible value silently
          forces overflow-y to "auto" too, which clips the absolutely
          positioned issue-details popup rendered inside the sticky first
          column - it was rendering, just invisible/clipped. */}
      <div className="hidden md:block overflow-x-auto overflow-y-visible">
        <DataTable
          tableName="Booking-table"
          key={`${sanitizedData.map((b) => b.id || b.bookingId).join(",")}-${isUpcomingBooking ? "up" : "prev"}`}
        >
          <thead>
            <tr>
              {[
                "Booking ID",
                "Name",
                "Services",
                "Assigned Employee",
                "Date/Time",
                "Amount",
                "Status",
                "Addresses",
                "Payment Type",
                "Phone Number",
                "Invoice Number",
                "Taxable Amount",
                "GST",
              ].map((col) => (
                <th
                  key={col}
                  className={`text-white whitespace-nowrap px-4 py-2 ${
                    col === "Booking ID"
                      ? "sticky left-0 bg-[#0c0b0b] z-10"
                      : ""
                  } ${
                    ["Payment Status"].includes(col)
                      ? "hidden md:table-cell"
                      : ""
                  }`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sanitizedData.map((booking) => {
              const rowKey =
                booking.id || booking.bookingId || `bk-${Math.random()}`;
              const idKey = booking.id || booking.bookingId;
              return (
                <React.Fragment key={rowKey}>
                  <tr
                    key={rowKey}
                    className="relative border-b border-[#E0E0E0] hover:bg-[#1a1a1a]"
                  >
                    <td className="sticky left-0 z-20 bg-[#0c0b0b]">
                      <div className="flex items-center gap-2 px-4 py-2">
                        <span>{booking.bookingId || "—"}</span>

                        {/* 🟡 NEW / UNACKNOWLEDGED BADGE */}
                        {!booking.isAdminAcknowledged && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-400 text-black animate-pulse">
                            New
                          </span>
                        )}

                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-[#FBB033] text-black">
                          {booking.bookingType === "admin" ? "Admin" : "User"}
                        </span>
                        {booking.paymentStatus === "Completed" && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-600 text-white">
                            Paid
                          </span>
                        )}

                        {/* 🔴 ISSUE BADGE */}
                        {booking.issueType &&
                          !booking.issueResolvedAt &&
                          isIssueFlaggable(booking) && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleIssuePopup(idKey, e);
                              }}
                              className="cursor-pointer px-2 py-0.5 text-xs font-semibold rounded-full bg-red-600 text-white animate-pulse"
                            >
                              Issue
                            </span>
                          )}
                        {/* 🟢 RESOLVED BADGE */}
                        {booking.issueType && booking.issueResolvedAt && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleIssuePopup(idKey, e);
                            }}
                            className="cursor-pointer px-2 py-0.5 text-xs font-semibold rounded-full bg-green-600 text-white"
                          >
                            Resolved
                          </span>
                        )}

                        {/* ⏳ NO-SHOW AWAITING RESPONSE BADGE */}
                        {booking.noShowHoldStatus === "AWAITING_RESPONSE" && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-black animate-pulse">
                            ⏳ Awaiting response
                          </span>
                        )}

                        {/* ⚠️ NO-SHOW HOLD BADGE - employee already freed, see no-show-hold-sweep.job.js */}
                        {booking.status === "OnHold" && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-600 text-white animate-pulse">
                            ⚠ No-show hold
                          </span>
                        )}

                        {/* ➕ ON-SITE ADD-ON BADGE - service(s) added after start, customer-code approved */}
                        {(booking.orderDetail || []).some((s) => s.addOnCount > 0) && (
                          <span
                            title="Has services added on-site after the service started"
                            className="px-2 py-0.5 text-xs font-semibold rounded-full bg-violet-600 text-white"
                          >
                            ➕ Add-on
                          </span>
                        )}

                        {/* ⋮ THREE-DOT ACTION MENU */}
                        <div className="relative ml-auto">
                          <button
                            className="text-gray-400 hover:text-white px-1 py-0.5 rounded text-lg leading-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuAnchor({ top: rect.bottom + 4, left: rect.left });
                              setOpenMenuId((prev) => (prev === idKey ? null : idKey));
                              // Close other overlays
                              setExpandedRemarkRow(null);
                              setExpandedPaymentRow(null);
                            }}
                            title="Actions"
                          >
                            ⋮
                          </button>

                        </div>
                      </div>
                    </td>

                    <td className="text-white px-4 py-2 whitespace-pre-wrap break-words max-w-[150px]">
                      {booking.user?.fullName || ""}
                      {booking.user?.isBlocked && (
                        <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-600/20 text-red-500 border border-red-500/40">
                          Blocked
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1 justify-start max-w-[200px]">
                        {(Array.isArray(booking.orderDetail)
                          ? booking.orderDetail
                          : []
                        ).map((item, idx) => (
                          <span
                            key={item?.id || `${rowKey}-od-${idx}`}
                            className="uppercase text-xs font-medium text-black bg-[#FBB033] rounded-2xl px-2 py-1 whitespace-nowrap"
                          >
                            {item?.name || "—"}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td className="text-white text-center px-4 py-2">
                      {booking.employee?.fullName || ""}
                      {booking.employee?.isDummy && (
                        <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-400/20 text-purple-400 border border-purple-400/40">
                          Dummy
                        </span>
                      )}
                    </td>

                    <td className="text-white text-center px-4 py-2 whitespace-nowrap">
                      {booking.bookingDate || ""}
                      {booking.timeSlot ? `, ${booking.timeSlot}` : ""}
                    </td>

                    <td className="text-white text-center px-4 py-2 whitespace-nowrap">
                      {typeof booking.price !== "undefined" ? (
                        <>
                          <div>₹ {booking.price}</div>
                          {booking.amountPaid > 0 && booking.price > booking.amountPaid && (
                            <div className="text-xs text-red-400 mt-1">
                              Pending: ₹ {booking.price - booking.amountPaid}
                            </div>
                          )}
                        </>
                      ) : (
                        ""
                      )}
                    </td>

                    <td className="text-white text-center px-4 py-2 truncate max-w-[120px]">
                      {booking.status || ""}
                    </td>

                    <td className="text-white text-center px-4 py-2 whitespace-pre-wrap break-words max-w-[200px]">
                      {booking.address || ""}
                    </td>

                    <td className="text-white text-center px-4 py-2 hidden md:table-cell">
                      {booking.paymentType || ""}
                    </td>

                    <td className="text-white text-center px-4 py-2 whitespace-nowrap">
                      {booking.user?.contactNumber || ""}
                    </td>
                    <td className="text-white text-center px-4 py-2 whitespace-nowrap">
                      {booking.invoiceNumber || ""}
                    </td>
                    <td className="text-white text-center px-4 py-2 whitespace-nowrap">
                      {typeof booking.taxableAmount !== "undefined" && booking.taxableAmount !== null
                        ? `₹ ${booking.taxableAmount}`
                        : "—"}
                    </td>
                    <td className="text-white text-center px-4 py-2 whitespace-nowrap">
                      {typeof booking.gst !== "undefined" && booking.gst !== null
                        ? `₹ ${booking.gst}`
                        : "—"}
                    </td>
                  </tr>

                </React.Fragment>
              );
            })}
          </tbody>
        </DataTable>
      </div>

      {/* Mobile: card view */}
      <div className="md:hidden flex flex-col gap-3 px-2">
        {sanitizedData.map((booking) => {
          const rowKey =
            booking.id || booking.bookingId || `bk-${Math.random()}`;
          const idKey = booking.id || booking.bookingId;
          const isDeleting = deletingIds.has(idKey);
          const hasIssue =
            booking.issueType &&
            !booking.issueResolvedAt &&
            isIssueFlaggable(booking);
          const isResolvedIssue = booking.issueType && booking.issueResolvedAt;

          return (
            <div
              key={rowKey}
              className="relative bg-[#0c0b0b] border border-[#2a2a2a] rounded-xl p-4 shadow"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header: booking id, badges, actions */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-white font-semibold text-sm">
                    {booking.bookingId || "—"}
                  </span>
                  {!booking.isAdminAcknowledged && (
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-400 text-black animate-pulse">
                      New
                    </span>
                  )}
                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-[#FBB033] text-black">
                    {booking.bookingType === "admin" ? "Admin" : "User"}
                  </span>
                  {booking.paymentStatus === "Completed" && (
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-600 text-white">
                      Paid
                    </span>
                  )}
                  {hasIssue && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleIssuePopup(idKey, e);
                      }}
                      className="cursor-pointer px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-600 text-white animate-pulse"
                    >
                      Issue
                    </span>
                  )}
                  {isResolvedIssue && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleIssuePopup(idKey, e);
                      }}
                      className="cursor-pointer px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-600 text-white"
                    >
                      Resolved
                    </span>
                  )}
                  {booking.noShowHoldStatus === "AWAITING_RESPONSE" && (
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-500 text-black animate-pulse">
                      ⏳ Awaiting response
                    </span>
                  )}
                  {booking.status === "OnHold" && (
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-600 text-white animate-pulse">
                      ⚠ No-show hold
                    </span>
                  )}
                  {(booking.orderDetail || []).some((s) => s.addOnCount > 0) && (
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-violet-600 text-white">
                      ➕ Add-on
                    </span>
                  )}
                </div>

                <div className="relative shrink-0">
                  <button
                    className="text-gray-400 hover:text-white px-1 py-0.5 rounded text-lg leading-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId((prev) => (prev === idKey ? null : idKey));
                      setExpandedRemarkRow(null);
                      setExpandedPaymentRow(null);
                    }}
                    title="Actions"
                  >
                    ⋮
                  </button>

                  {openMenuId === idKey && (
                    <div
                      className="absolute right-0 top-7 z-50 min-w-[160px] bg-[#1e1e1e] rounded-xl shadow-2xl border border-[#2a2a2a] py-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {can("bookings.edit") && (
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                          onClick={() => {
                            setOpenMenuId(null);
                            handleEditClick(booking);
                          }}
                        >
                          ✏️ Edit
                        </button>
                      )}

                      <button
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                        onClick={() => {
                          setOpenMenuId(null);
                          setViewingBooking(booking);
                        }}
                      >
                        📄 View Details
                      </button>

                      {can("bookings.edit") && (
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                          onClick={() => {
                            setOpenMenuId(null);
                            toggleRemarkRow(idKey);
                          }}
                        >
                          💬 Remarks
                          {booking.adminRemark
                            ? ` (${booking.adminRemark.split("\n\n").filter(Boolean).length})`
                            : ""}
                        </button>
                      )}

                      {can("bookings.edit") &&
                        Number(booking.price || 0) -
                          Number(booking.amountPaid || 0) >
                          0 && (
                          <button
                            className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                            onClick={() => {
                              setOpenMenuId(null);
                              togglePaymentRow(idKey);
                            }}
                          >
                            💰 Record Payment
                          </button>
                        )}

                      {can("bookings.edit") && !booking.isAdminAcknowledged && (
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-amber-400 hover:bg-[#2a2a2a] disabled:opacity-50"
                          disabled={acknowledgingIds.has(idKey)}
                          onClick={async () => {
                            setOpenMenuId(null);
                            setAcknowledgingIds((prev) => new Set(prev).add(idKey));
                            try {
                              const res = await acknowledgeBookingMutation({
                                variables: { data: idKey },
                              });
                              if (res.data?.acknowledgeBooking?.status === "SUCCESS") {
                                setSanitizedData((prev) =>
                                  prev.map((b) =>
                                    (b.id || b.bookingId) === idKey
                                      ? { ...b, isAdminAcknowledged: true, adminAcknowledgedAt: new Date().toISOString() }
                                      : b
                                  )
                                );
                              }
                            } catch (err) {
                              console.error("Failed to acknowledge booking:", err);
                            } finally {
                              setAcknowledgingIds((prev) => {
                                const next = new Set(prev);
                                next.delete(idKey);
                                return next;
                              });
                            }
                          }}
                        >
                          {acknowledgingIds.has(idKey) ? "Acking..." : "✅ Ack"}
                        </button>
                      )}

                      {can("bookings.edit") && booking.issueType && !booking.issueResolvedAt && (
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-[#2a2a2a]"
                          onClick={() => {
                            setOpenMenuId(null);
                            handleResolveClick(booking);
                          }}
                        >
                          🟢 Resolve Issue
                        </button>
                      )}

                      {can("bookings.delete") && (
                        <>
                          <div className="my-1 border-t border-[#333]" />
                          <button
                            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-[#2a2a2a] disabled:opacity-50"
                            onClick={() => {
                              setOpenMenuId(null);
                              handleDeleteClick(booking);
                            }}
                            disabled={isDeleting}
                          >
                            {isDeleting ? "🗑 Deleting..." : "🗑 Delete"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Status pill */}
              <div className="mt-2">
                <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-[#1a1a1a] text-[#FBB033] border border-[#333]">
                  {booking.status || "—"}
                </span>
              </div>

              {/* Summary: employee, timings, amount — always visible */}
              <div className="mt-3 grid grid-cols-3 gap-x-2 gap-y-2 text-sm">
                <div>
                  <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                    Employee
                  </div>
                  <div className="text-white break-words">
                    {booking.employee?.fullName || "—"}
                    {booking.employee?.isDummy && (
                      <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-purple-400/20 text-purple-400 border border-purple-400/40">
                        Dummy
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                    Timings
                  </div>
                  <div className="text-white">
                    {booking.bookingDate || ""}
                    {booking.timeSlot ? (
                      <div className="whitespace-nowrap">{booking.timeSlot}</div>
                    ) : null}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                    Amount
                  </div>
                  <div className="text-white">
                    {typeof booking.price !== "undefined"
                      ? `₹ ${booking.price}`
                      : "—"}
                    {booking.amountPaid > 0 &&
                      booking.price > booking.amountPaid && (
                        <div className="text-xs text-red-400">
                          Due ₹ {booking.price - booking.amountPaid}
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Toggle: show/hide the rest of the booking details */}
              <button
                type="button"
                className="mt-3 w-full flex items-center justify-center gap-1 text-xs font-semibold text-[#FBB033] border-t border-[#2a2a2a] pt-2"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCardDetails(idKey);
                }}
              >
                {expandedCardId === idKey ? "Hide details ▲" : "View details ▼"}
              </button>

              {expandedCardId === idKey && (
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <div>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Name
                    </div>
                    <div className="text-white break-words">
                      {booking.user?.fullName || "—"}
                      {booking.user?.isBlocked && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-red-600/20 text-red-500 border border-red-500/40">
                          Blocked
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Phone
                    </div>
                    <div className="text-white">
                      {booking.user?.contactNumber || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Payment Type
                    </div>
                    <div className="text-white">
                      {booking.paymentType || "—"}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Address
                    </div>
                    <div className="text-white break-words">
                      {booking.address || "—"}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Services
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(Array.isArray(booking.orderDetail)
                        ? booking.orderDetail
                        : []
                      ).map((item, idx) => (
                        <span
                          key={item?.id || `${rowKey}-mcard-${idx}`}
                          className="uppercase text-[10px] font-medium text-black bg-[#FBB033] rounded-2xl px-2 py-1 whitespace-nowrap"
                        >
                          {item?.name || "—"}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Issue details (expands inline, full card width) */}
              {booking.issueType && expandedRow === idKey && (
                <div className="mt-3 bg-[#1e1e1e] rounded-xl p-4 border border-[#2a2a2a] border-l-4 border-l-red-600">
                  {booking.needsReassignment && (
                    <div className="mb-3 flex items-center gap-2 bg-red-600/20 border border-red-600/50 text-red-400 text-xs font-semibold px-3 py-2 rounded-lg">
                      ⚠️ Needs Reassignment - professional couldn't continue this booking
                    </div>
                  )}

                  <div className="flex justify-between items-center mb-3 gap-4">
                    <h3 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide">
                      Reported Issue
                    </h3>
                    <span className="text-xs text-gray-400">
                      {booking.issueReportedAt
                        ? new Date(booking.issueReportedAt).toLocaleString()
                        : "No Date"}
                    </span>
                  </div>

                  <div className="mb-3">
                    <span className="text-xs text-gray-400">Issue Type </span>
                    <div className="mt-1 inline-block px-3 py-1 text-xs font-semibold rounded-full bg-red-600 text-white">
                      {formatIssueType(booking.issueType)}
                    </div>
                  </div>

                  <div>
                    <span className="text-xs text-gray-400">Description</span>
                    <p className="mt-2 text-sm text-white bg-[#2a2a2a] p-3 rounded-lg leading-relaxed whitespace-pre-wrap">
                      {booking.issueDescription || "No description provided"}
                    </p>
                  </div>

                  {Array.isArray(booking.attemptedServices) &&
                    booking.attemptedServices.length > 0 && (
                      <div className="mt-3">
                        <span className="text-xs text-gray-400">Services at time of interruption</span>
                        <div className="mt-2 space-y-1">
                          {booking.attemptedServices.map((service, i) => (
                            <div
                              key={service.serviceId || i}
                              className="flex items-center justify-between bg-[#2a2a2a] rounded-lg px-3 py-1.5 text-sm"
                            >
                              <span className="text-white">{service.name || "Service"}</span>
                              <span
                                className={
                                  service.completed
                                    ? "text-green-400 text-xs font-semibold"
                                    : "text-amber-400 text-xs font-semibold"
                                }
                              >
                                {service.completed ? "✓ Completed" : "○ Not completed"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {booking.issueResolvedAt && (
                    <div className="mt-4 border-t border-[#333] pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-green-400 font-semibold">
                          Resolved At
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(booking.issueResolvedAt).toLocaleString()}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        Resolution Details
                      </span>
                      <p className="mt-1 text-sm text-white bg-[#2a2a2a] p-3 rounded-lg leading-relaxed border-l-2 border-green-500 whitespace-pre-wrap">
                        {booking.issueResolutionDetails || "Issue resolved"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Remarks (expands inline, full card width) */}
              {expandedRemarkRow === idKey && (
                <div
                  className="mt-3 bg-[#1e1e1e] rounded-xl p-4 border border-[#2a2a2a] border-l-4 border-l-[#FBB033] text-left"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-3 gap-4">
                    <h3 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide">
                      Admin Remarks
                    </h3>
                    <button
                      className="text-gray-400 hover:text-white text-xs"
                      onClick={() => setExpandedRemarkRow(null)}
                    >
                      ✕
                    </button>
                  </div>

                  {booking.adminRemark ? (
                    <div className="space-y-2 mb-3 max-h-48 overflow-y-auto pr-1">
                      {booking.adminRemark
                        .split("\n\n")
                        .filter(Boolean)
                        .map((entry, i) => {
                          const timestampMatch = entry.match(/^\[(.*?)\]\s*/);
                          const timestamp = timestampMatch
                            ? timestampMatch[1]
                            : null;
                          const text = timestampMatch
                            ? entry.slice(timestampMatch[0].length)
                            : entry;

                          return (
                            <div
                              key={i}
                              className="bg-[#2a2a2a] rounded p-2 text-sm text-white"
                            >
                              <span className="text-[#FBB033] font-semibold mr-1">
                                Comment {i + 1}:
                              </span>
                              {text}
                              {timestamp && (
                                <div className="text-gray-400 text-xs mt-1">
                                  {timestamp}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm mb-3">No remarks yet.</p>
                  )}

                  <textarea
                    value={remarkDrafts[idKey] || ""}
                    onChange={(e) =>
                      setRemarkDrafts((prev) => ({
                        ...prev,
                        [idKey]: e.target.value,
                      }))
                    }
                    className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] min-h-[60px] text-sm"
                    placeholder="Add a note about this booking..."
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      className="bg-[#FBB033] text-black px-4 py-1.5 rounded-md font-bold text-xs disabled:opacity-50 hover:bg-[#e5a02f] transition"
                      disabled={
                        savingRemarkIds.has(idKey) ||
                        !(remarkDrafts[idKey] || "").trim()
                      }
                      onClick={() => handleAddRemark(booking)}
                    >
                      {savingRemarkIds.has(idKey) ? "Saving..." : "Add Remark"}
                    </button>
                  </div>
                </div>
              )}

              {/* Record payment (expands inline, full card width) */}
              {expandedPaymentRow === idKey && (
                <div
                  className="mt-3 bg-[#1e1e1e] rounded-xl p-4 border border-[#2a2a2a] border-l-4 border-l-[#FBB033] text-left"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-3 gap-4">
                    <h3 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide">
                      Record Online Payment
                    </h3>
                    <button
                      className="text-gray-400 hover:text-white text-xs"
                      onClick={() => setExpandedPaymentRow(null)}
                    >
                      ✕
                    </button>
                  </div>

                  <p className="text-xs text-gray-400 mb-3">
                    Collected so far: ₹{booking.amountPaid || 0} &nbsp;|&nbsp;
                    Pending: ₹
                    {Math.max(
                      Number(booking.price || 0) -
                        Number(booking.amountPaid || 0),
                      0,
                    )}
                  </p>

                  <div className="flex flex-col gap-2">
                    <input
                      type="number"
                      min="0"
                      value={getPaymentDraft(idKey).amount}
                      onChange={(e) =>
                        updatePaymentDraft(idKey, "amount", e.target.value)
                      }
                      placeholder="Amount (₹)"
                      className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] text-sm"
                    />
                    <input
                      type="text"
                      value={getPaymentDraft(idKey).referenceId}
                      onChange={(e) =>
                        updatePaymentDraft(idKey, "referenceId", e.target.value)
                      }
                      placeholder="Razorpay Ref / UTR (optional)"
                      className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] text-sm"
                    />
                    <input
                      type="text"
                      value={getPaymentDraft(idKey).note}
                      onChange={(e) =>
                        updatePaymentDraft(idKey, "note", e.target.value)
                      }
                      placeholder="Note (optional)"
                      className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] text-sm"
                    />
                  </div>

                  <div className="flex justify-end mt-2">
                    <button
                      className="bg-[#FBB033] text-black px-4 py-1.5 rounded-md font-bold text-xs disabled:opacity-50 hover:bg-[#e5a02f] transition"
                      disabled={
                        savingPaymentIds.has(idKey) ||
                        !getPaymentDraft(idKey).amount
                      }
                      onClick={() => handleRecordPayment(booking)}
                    >
                      {savingPaymentIds.has(idKey) ? "Saving..." : "Save Payment"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {sanitizedData.length === 0 && !loading && (
          <div className="text-center text-gray-400 text-sm py-8">
            No bookings found.
          </div>
        )}
      </div>

      {viewingBooking && (
        <OrderDetailsModal
          booking={viewingBooking}
          onClose={() => setViewingBooking(null)}
        />
      )}

      {expandedRow &&
        issuePopupAnchor &&
        (() => {
          const issueBooking = sanitizedData.find(
            (b) => (b.id || b.bookingId) === expandedRow,
          );
          if (!issueBooking?.issueType) return null;
          return createPortal(
            <div
              className="fixed z-[9999] w-max max-w-[650px] bg-[#1e1e1e] rounded-xl p-5 shadow-2xl border border-[#2a2a2a]"
              style={{ top: issuePopupAnchor.top, left: issuePopupAnchor.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute left-0 top-0 h-full w-1 bg-red-600 rounded-l-xl" />

              {issueBooking.needsReassignment && (
                <div className="mb-3 flex items-center gap-2 bg-red-600/20 border border-red-600/50 text-red-400 text-xs font-semibold px-3 py-2 rounded-lg">
                  ⚠️ Needs Reassignment - professional couldn't continue this booking
                </div>
              )}

              <div className="flex justify-between items-center mb-4 gap-8">
                <h3 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide">
                  Reported Issue
                </h3>
                <span className="text-xs text-gray-400">
                  {issueBooking.issueReportedAt
                    ? new Date(issueBooking.issueReportedAt).toLocaleString()
                    : "No Date"}
                </span>
              </div>

              <div className="mb-3">
                <span className="text-xs text-gray-400">Issue Type </span>
                <div className="mt-1 inline-block px-3 py-1 text-xs font-semibold rounded-full bg-red-600 text-white">
                  {formatIssueType(issueBooking.issueType)}
                </div>
              </div>

              <div>
                <span className="text-xs text-gray-400">Description</span>
                <p className="mt-2 text-sm text-white bg-[#2a2a2a] p-3 rounded-lg leading-relaxed whitespace-pre-wrap max-w-[400px]">
                  {issueBooking.issueDescription || "No description provided"}
                </p>
              </div>

              {Array.isArray(issueBooking.attemptedServices) &&
                issueBooking.attemptedServices.length > 0 && (
                  <div className="mt-3">
                    <span className="text-xs text-gray-400">Services at time of interruption</span>
                    <div className="mt-2 space-y-1 max-w-[400px]">
                      {issueBooking.attemptedServices.map((service, i) => (
                        <div
                          key={service.serviceId || i}
                          className="flex items-center justify-between bg-[#2a2a2a] rounded-lg px-3 py-1.5 text-sm"
                        >
                          <span className="text-white">{service.name || "Service"}</span>
                          <span
                            className={
                              service.completed
                                ? "text-green-400 text-xs font-semibold"
                                : "text-amber-400 text-xs font-semibold"
                            }
                          >
                            {service.completed ? "✓ Completed" : "○ Not completed"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {issueBooking.issueResolvedAt && (
                <div className="mt-4 border-t border-[#333] pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-green-400 font-semibold">
                      Resolved At
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(issueBooking.issueResolvedAt).toLocaleString()}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">Resolution Details</span>
                  <p className="mt-1 text-sm text-white bg-[#2a2a2a] p-3 rounded-lg leading-relaxed border-l-2 border-green-500 whitespace-pre-wrap max-w-[400px]">
                    {issueBooking.issueResolutionDetails || "Issue resolved"}
                  </p>
                </div>
              )}
            </div>,
            document.body,
          );
        })()}

      {menuAnchor &&
        (() => {
          const menuBooking = openMenuId
            ? sanitizedData.find((b) => (b.id || b.bookingId) === openMenuId)
            : null;
          const remarkBooking = expandedRemarkRow
            ? sanitizedData.find((b) => (b.id || b.bookingId) === expandedRemarkRow)
            : null;
          const paymentBooking = expandedPaymentRow
            ? sanitizedData.find((b) => (b.id || b.bookingId) === expandedPaymentRow)
            : null;
          return (
            <>
              {openMenuId &&
                menuBooking &&
                createPortal(
                            <div
                              className="fixed z-[9999] min-w-[140px] bg-[#1e1e1e] rounded-xl shadow-2xl border border-[#2a2a2a] py-1"
                              style={{ top: menuAnchor.top, left: menuAnchor.left }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Edit */}
                              {can("bookings.edit") && (
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    handleEditClick(menuBooking);
                                  }}
                                >
                                  ✏️ Edit
                                </button>
                              )}

                              {/* View Details (read-only, tabbed: order/service/inventory/payment) */}
                              <button
                                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setViewingBooking(menuBooking);
                                }}
                              >
                                📄 View Details
                              </button>

                              {/* Remarks */}
                              {can("bookings.edit") && (
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    toggleRemarkRow(openMenuId);
                                  }}
                                >
                                  💬 Remarks
                                  {menuBooking.adminRemark
                                    ? ` (${menuBooking.adminRemark.split("\n\n").filter(Boolean).length})`
                                    : ""}
                                </button>
                              )}

                              {/* Record Payment (admin QR paid outside the
                                  per-booking flow) */}
                              {can("bookings.edit") &&
                                Number(menuBooking.price || 0) -
                                  Number(menuBooking.amountPaid || 0) >
                                  0 && (
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2a2a2a]"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      togglePaymentRow(openMenuId);
                                    }}
                                  >
                                    💰 Record Payment
                                  </button>
                                )}

                              {/* Ack */}
                              {can("bookings.edit") && !menuBooking.isAdminAcknowledged && (
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-amber-400 hover:bg-[#2a2a2a] disabled:opacity-50"
                                  disabled={acknowledgingIds.has(openMenuId)}
                                  onClick={async () => {
                                    setOpenMenuId(null);
                                    setAcknowledgingIds((prev) => new Set(prev).add(openMenuId));
                                    try {
                                      const res = await acknowledgeBookingMutation({
                                        variables: { data: openMenuId },
                                      });
                                      if (res.data?.acknowledgeBooking?.status === "SUCCESS") {
                                        setSanitizedData((prev) =>
                                          prev.map((b) =>
                                            (b.id || b.bookingId) === openMenuId
                                              ? { ...b, isAdminAcknowledged: true, adminAcknowledgedAt: new Date().toISOString() }
                                              : b
                                          )
                                        );
                                      }
                                    } catch (err) {
                                      console.error("Failed to acknowledge booking:", err);
                                    } finally {
                                      setAcknowledgingIds((prev) => {
                                        const next = new Set(prev);
                                        next.delete(openMenuId);
                                        return next;
                                      });
                                    }
                                  }}
                                >
                                  {acknowledgingIds.has(openMenuId) ? "Acking..." : "✅ Ack"}
                                </button>
                              )}

                              {/* Resolve Issue */}
                              {can("bookings.edit") && menuBooking.issueType && !menuBooking.issueResolvedAt && (
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-[#2a2a2a]"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    handleResolveClick(menuBooking);
                                  }}
                                >
                                  🟢 Resolve Issue
                                </button>
                              )}

                              {/* Delete */}
                              {can("bookings.delete") && (
                                <>
                                  <div className="my-1 border-t border-[#333]" />
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-[#2a2a2a] disabled:opacity-50"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleDeleteClick(menuBooking);
                                    }}
                                    disabled={deletingIds.has(openMenuId)}
                                  >
                                    {deletingIds.has(openMenuId) ? "🗑 Deleting..." : "🗑 Delete"}
                                  </button>
                                </>
                              )}
                            </div>
                              ,
                  document.body,
                )}
              {expandedRemarkRow &&
                remarkBooking &&
                createPortal(
                            <div
                              className="fixed z-[9999] w-max max-w-[400px] bg-[#1e1e1e] rounded-xl p-4 shadow-2xl border border-[#2a2a2a] text-left"
                              style={{ top: menuAnchor.top, left: menuAnchor.left }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="absolute left-0 top-0 h-full w-1 bg-[#FBB033] rounded-l-xl" />

                              <div className="flex justify-between items-center mb-3 gap-8">
                                <h3 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide">
                                  Admin Remarks
                                </h3>
                                <button
                                  className="text-gray-400 hover:text-white text-xs"
                                  onClick={() => setExpandedRemarkRow(null)}
                                >
                                  ✕
                                </button>
                              </div>

                              {remarkBooking.adminRemark ? (
                                <div className="space-y-2 mb-3 max-h-48 overflow-y-auto pr-1">
                                  {remarkBooking.adminRemark
                                    .split("\n\n")
                                    .filter(Boolean)
                                    .map((entry, i) => {
                                      const timestampMatch = entry.match(/^\[(.*?)\]\s*/);
                                      const timestamp = timestampMatch
                                        ? timestampMatch[1]
                                        : null;
                                      const text = timestampMatch
                                        ? entry.slice(timestampMatch[0].length)
                                        : entry;

                                      return (
                                        <div
                                          key={i}
                                          className="bg-[#2a2a2a] rounded p-2 text-sm text-white"
                                        >
                                          <span className="text-[#FBB033] font-semibold mr-1">
                                            Comment {i + 1}:
                                          </span>
                                          {text}
                                          {timestamp && (
                                            <div className="text-gray-400 text-xs mt-1">
                                              {timestamp}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              ) : (
                                <p className="text-gray-400 text-sm mb-3">
                                  No remarks yet.
                                </p>
                              )}

                              <textarea
                                value={remarkDrafts[expandedRemarkRow] || ""}
                                onChange={(e) =>
                                  setRemarkDrafts((prev) => ({
                                    ...prev,
                                    [expandedRemarkRow]: e.target.value,
                                  }))
                                }
                                className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] min-h-[60px] text-sm"
                                placeholder="Add a note about this booking..."
                              />
                              <div className="flex justify-end mt-2">
                                <button
                                  className="bg-[#FBB033] text-black px-4 py-1.5 rounded-md font-bold text-xs disabled:opacity-50 hover:bg-[#e5a02f] transition"
                                  disabled={
                                    savingRemarkIds.has(expandedRemarkRow) ||
                                    !(remarkDrafts[expandedRemarkRow] || "").trim()
                                  }
                                  onClick={() => handleAddRemark(remarkBooking)}
                                >
                                  {savingRemarkIds.has(expandedRemarkRow)
                                    ? "Saving..."
                                    : "Add Remark"}
                                </button>
                              </div>
                            </div>
                              ,
                  document.body,
                )}
              {expandedPaymentRow &&
                paymentBooking &&
                createPortal(
                            <div
                              className="fixed z-[9999] w-max max-w-[360px] bg-[#1e1e1e] rounded-xl p-4 shadow-2xl border border-[#2a2a2a] text-left"
                              style={{ top: menuAnchor.top, left: menuAnchor.left }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="absolute left-0 top-0 h-full w-1 bg-[#FBB033] rounded-l-xl" />

                              <div className="flex justify-between items-center mb-3 gap-8">
                                <h3 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide">
                                  Record Online Payment
                                </h3>
                                <button
                                  className="text-gray-400 hover:text-white text-xs"
                                  onClick={() => setExpandedPaymentRow(null)}
                                >
                                  ✕
                                </button>
                              </div>

                              <p className="text-xs text-gray-400 mb-3">
                                Collected so far: ₹{paymentBooking.amountPaid || 0}{" "}
                                &nbsp;|&nbsp; Pending: ₹
                                {Math.max(
                                  Number(paymentBooking.price || 0) -
                                    Number(paymentBooking.amountPaid || 0),
                                  0,
                                )}
                              </p>

                              <div className="flex flex-col gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  value={getPaymentDraft(expandedPaymentRow).amount}
                                  onChange={(e) =>
                                    updatePaymentDraft(
                                      expandedPaymentRow,
                                      "amount",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Amount (₹)"
                                  className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] text-sm"
                                />
                                <input
                                  type="text"
                                  value={getPaymentDraft(expandedPaymentRow).referenceId}
                                  onChange={(e) =>
                                    updatePaymentDraft(
                                      expandedPaymentRow,
                                      "referenceId",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Razorpay Ref / UTR (optional)"
                                  className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] text-sm"
                                />
                                <input
                                  type="text"
                                  value={getPaymentDraft(expandedPaymentRow).note}
                                  onChange={(e) =>
                                    updatePaymentDraft(
                                      expandedPaymentRow,
                                      "note",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Note (optional)"
                                  className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB033] text-sm"
                                />
                              </div>

                              <div className="flex justify-end mt-2">
                                <button
                                  className="bg-[#FBB033] text-black px-4 py-1.5 rounded-md font-bold text-xs disabled:opacity-50 hover:bg-[#e5a02f] transition"
                                  disabled={
                                    savingPaymentIds.has(expandedPaymentRow) ||
                                    !getPaymentDraft(expandedPaymentRow).amount
                                  }
                                  onClick={() => handleRecordPayment(paymentBooking)}
                                >
                                  {savingPaymentIds.has(expandedPaymentRow)
                                    ? "Saving..."
                                    : "Save Payment"}
                                </button>
                              </div>
                            </div>
                              ,
                  document.body,
                )}
            </>
          );
        })()}
    </div>
  );
};

export default React.memo(BookingTable);
